import Lottie from 'lottie-react'
import {Modal, Spinner, Button, Form, Badge} from 'react-bootstrap'
import searchLottie from '../../assets/lotties/search-leads.json'
import {IResContactsPreviewAPI, PolicySummary, MemberPolicySummary} from '../../types/contactsApi'
import React, {CSSProperties} from 'react'
import {hasPermission} from 'app/tfi/helpers'
import {ALL_PERMISSIONS, PAYMENT_STATUS_OPTIONS, getPaymentVariant} from 'app/tfi/helpers/consts'
import {useGetUser} from 'app/tfi/store/userStore'
import {useMutation, useQuery} from '@apollo/client'
import {REQUEST_POLICY_ASSIGNMENT} from '../../gql/mutations/policyMutation'
import {IRequestPolicyAssignmentResponse, AllPolicyStatusesData} from '../../types/policies'
import {toast} from 'react-toastify'
import {parseId} from '../../helpers'
import {BadgeStatePolicy} from '../../pages/onePolicy/components/BadgeStatePolicy'
import {GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {getColorByStatus, getTitleByStatus} from '../../pages/shared/utils'

export const ModalBodySearchContactHeader: React.FC<{
  data: undefined | IResContactsPreviewAPI
  loading: boolean
  keyword: string
  onClose: () => void
}> = ({data, loading, onClose, keyword}) => {
  const permissions = useGetUser((user) => user?.user?.permissions)
  const userGroups = useGetUser((user) => user?.user?.groups) || []
  const currentUser = useGetUser((user) => user?.user?.user)
  const currentUserId = currentUser?.id ? parseInt(`${currentUser.id}`) : null
  const isAsesor = userGroups.includes('Asesor')
  const isAgente = userGroups.includes('Agente')
  const canViewAllPolicyStatuses =
    !!permissions && hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY_STATUS)
  const isRestrictedPolicyStatus = isAgente && !canViewAllPolicyStatuses

  const [requestContext, setRequestContext] = React.useState<{
    policyId: string
    policyName: string
    assignedName: string | null
    contactName: string
  } | null>(null)
  const [requestReason, setRequestReason] = React.useState('')
  const [showExtraYear, setShowExtraYear] = React.useState(false)
  const bodyRef = React.useRef<HTMLDivElement | null>(null)

  const [requestPolicyAssignment, {loading: requestingAssignment}] =
    useMutation<IRequestPolicyAssignmentResponse>(REQUEST_POLICY_ASSIGNMENT)
  const {data: policyStatusesData} = useQuery<AllPolicyStatusesData>(GET_ALL_POLICIES_STATUSES, {
    variables: {first: 100},
  })

  React.useEffect(() => {
    const dialog = bodyRef.current?.closest('.modal-dialog') as HTMLElement | null
    if (dialog) {
      dialog.style.setProperty('--bs-modal-width', '95vw')
      dialog.style.maxWidth = '95vw'
      dialog.style.width = '95vw'
    }
  }, [])

  if (!permissions) return null

  const highlightKeyword = (text: string, color: CSSProperties['color'] = 'yellow') => {
    if (!keyword) return text
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Usamos una expresión regular para encontrar todas las ocurrencias de la palabra clave
    const regex = new RegExp(`(${escapedKeyword})`, 'gi')
    // Reemplazamos las ocurrencias con la palabra clave envuelta en un span con un estilo de fondo amarillo
    return text.replace(regex, `<span style="background-color: ${color};">$1</span>`)
  }

  if (loading) {
    return (
      <Modal.Body style={{minHeight: '300px'}}>
        <div className='d-flex align-items-center justify-content-center w-100 h-100'>
          <Spinner animation='border' />
        </div>
      </Modal.Body>
    )
  }
  if (!data) {
    return (
      <Modal.Body
        style={{minHeight: '300px'}}
        className='d-flex align-items-center justify-content-center gap-5 flex-column'
      >
        You can find your Contacts by entering phone number or name
        <Lottie animationData={searchLottie} autoPlay />
      </Modal.Body>
    )
  }
  const getPolicyTimestamp = (policy: PolicySummary): number => {
    const effective = policy.effectiveDate ? Date.parse(policy.effectiveDate) : NaN
    const created = policy.created ? Date.parse(policy.created) : NaN
    const timestamps = [effective, created].filter((value) => !Number.isNaN(value))
    return timestamps.length > 0 ? Math.max(...timestamps) : Number.NEGATIVE_INFINITY
  }

  const collectPolicy = (member?: MemberPolicySummary | null): PolicySummary | null => {
    if (!member?.policy || !member.policy.id) return null
    return member.policy
  }

  const collectPolicies = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node']
  ): PolicySummary[] => {
    const policyMap = new Map<string, PolicySummary>()
    const primaryPolicy = collectPolicy(node.memberPolicy)
    if (primaryPolicy) policyMap.set(primaryPolicy.id, primaryPolicy)

    node.members?.edges?.forEach(({node: memberNode}) => {
      const memberPolicy = collectPolicy(memberNode)
      if (memberPolicy) policyMap.set(memberPolicy.id, memberPolicy)
    })

    return Array.from(policyMap.values())
  }

  const getLatestPolicy = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node']
  ): PolicySummary | null => {
    const policies = collectPolicies(node)
    if (policies.length === 0) return null

    return policies.sort((a, b) => getPolicyTimestamp(b) - getPolicyTimestamp(a)).at(0) || null
  }

  const formatUserName = (user?: {firstName: string | null; lastName: string | null} | null) => {
    if (!user) return ''
    return `${user.firstName || ''} ${user.lastName || ''}`.trim()
  }

  const isCancelledPolicy = (policy?: PolicySummary | null): boolean => {
    const status = policy?.status || ''
    return status.toUpperCase().includes('CANCEL')
  }

  const isPolicyActive = (policy: PolicySummary, now: Date): boolean => {
    if (isCancelledPolicy(policy)) return false
    if (!policy.effectiveDate) return false

    const start = new Date(policy.effectiveDate)
    if (Number.isNaN(start.getTime())) return false

    const end = new Date(start.getTime())
    end.setUTCFullYear(end.getUTCFullYear() + 1)

    return now >= start && now < end
  }

  // Obtiene la póliza activa (tipo POLICY y no cancelada) para el año indicado
  const getPolicyForYear = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node'],
    targetYear: number
  ): PolicySummary | null => {
    const policies = collectPolicies(node).filter((policy) => {
      if (!policy.effectiveDate) return false

      const effectiveYear = new Date(policy.effectiveDate).getUTCFullYear()
      if (effectiveYear !== targetYear) return false
      if (isCancelledPolicy(policy)) return false

      return true
    })

    if (policies.length === 0) return null

    return policies.sort((a, b) => getPolicyTimestamp(b) - getPolicyTimestamp(a)).at(0) || null
  }

  const getPaymentStatusLabel = (value?: string | null) =>
    PAYMENT_STATUS_OPTIONS.find((option) => option.value === value)?.label || value || '-'

  const now = new Date()
  const currentYear = now.getUTCFullYear()
  const currentMonth = now.getMonth() // 0-11 (0 = Enero, 9 = Octubre)
  const previousYear = currentYear - 1
  const nextYear = currentYear + 1

  // De octubre a diciembre: mostrar año actual y siguiente (oculto: año anterior)
  // De enero a septiembre: mostrar año anterior y actual (oculto: año siguiente)
  const isOctoberToDecember = currentMonth >= 9
  const firstYearToShow = isOctoberToDecember ? currentYear : previousYear
  const secondYearToShow = isOctoberToDecember ? nextYear : currentYear

  // El año extra que se puede mostrar/ocultar con el toggle
  const extraYearToShow = isOctoberToDecember ? previousYear : nextYear

  // Toma la póliza más reciente (por fecha effective/created) que sea tipo POLICY y no cancelada
  const getLatestActivePolicy = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node'],
    now: Date
  ): PolicySummary | null => {
    return (
      collectPolicies(node)
        .filter((policy) => isPolicyActive(policy, now))
        .sort((a, b) => getPolicyTimestamp(b) - getPolicyTimestamp(a))
        .at(0) || null
    )
  }

  const statusEdges = policyStatusesData?.allPolicyStatuses?.edges ?? []
  const visibleStatusEdges = isRestrictedPolicyStatus
    ? statusEdges.filter(({node}) => node.enabledForAgents)
    : statusEdges
  const getStatusColor = (policy?: PolicySummary | null) => {
    return getColorByStatus(visibleStatusEdges, policy?.status || '')
  }
  const canShowPolicyStatus = (statusCode: string) => {
    if (!statusCode) return false
    if (!isRestrictedPolicyStatus) return true
    return visibleStatusEdges.some(({node}) => node.code === statusCode)
  }

  const renderPolicyCell = (policy: PolicySummary | null, mutedText?: string) => {
    if (!policy) {
      return mutedText ? <span className='badge badge-secondary'>{mutedText}</span> : '-'
    }

    const isArchivedPolicy = !!policy.archivedDate || policy.isArchived === true

    return (
      <div className='d-flex flex-column gap-1 align-items-start'>
        {canShowPolicyStatus(policy.status || '') && (
          <BadgeStatePolicy
            status={getTitleByStatus(visibleStatusEdges, policy.status || '')}
            color={getStatusColor(policy)}
          />
        )}
        <span className='d-block'>
          Payment:{' '}
          <Badge className='badge-sm' bg={getPaymentVariant(policy.paymentStatus || undefined)}>
            {policy.paymentStatus || 'N/A'}
          </Badge>
        </span>
        {isArchivedPolicy && (
          <Badge bg='dark' style={{color: '#fff'}}>
            Archived
          </Badge>
        )}
      </div>
    )
  }

  const handleOpenRequestModal = (
    policy: PolicySummary,
    assignedName: string | null,
    contactName: string
  ) => {
    setRequestContext({
      policyId: policy.id,
      policyName: policy.policyName || `Policy #${parseId(policy.id)}`,
      assignedName,
      contactName,
    })
    setRequestReason('')
  }

  const handleSubmitRequest = async () => {
    if (!requestContext) return
    try {
      const {data: mutationData} = await requestPolicyAssignment({
        variables: {
          policyId: parseId(requestContext.policyId),
          reason: requestReason.trim() === '' ? null : requestReason.trim(),
        },
      })

      const payload = mutationData?.requestPolicyAssignment
      if (payload?.success) {
        toast.success(payload.message || 'Assignment request sent')
        setRequestContext(null)
        return
      }

      const errorMessage =
        payload?.errors?.[0]?.message || 'No se pudo enviar la solicitud, intente más tarde'
      toast.error(errorMessage)
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo enviar la solicitud')
    }
  }

  return (
    <Modal.Body
      ref={bodyRef}
      style={{minHeight: '300px', maxHeight: '70vh', overflowY: 'auto', width: '100%'}}
      className='w-100'
    >
      <div
        className='table-responsive'
        style={{width: '100%', maxHeight: '60vh', overflow: 'auto', minWidth: '1300px'}}
      >
        <table className='table table-striped align-middle'>
          <thead className='bg-white sticky-top' style={{top: 0, zIndex: 1}}>
            <tr>
              <th colSpan={100} className='border-0 pb-2'>
                <div className='d-flex justify-content-end'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={() => setShowExtraYear(!showExtraYear)}
                  >
                    <i className={`bi ${showExtraYear ? 'bi-eye-slash' : 'bi-eye'} me-1`} />
                    {showExtraYear ? 'Hide' : 'Show'} {extraYearToShow} column
                  </Button>
                </div>
              </th>
            </tr>
            <tr>
              <th>Full Name</th>
              <th>Age</th>
              <th>Agent</th>
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT_PREVIEW_ASSIGNED) && (
                <th>Assigned To</th>
              )}
              <th>Active Policy</th>
              {showExtraYear && <th>Policy {extraYearToShow}</th>}
              <th>Policy {firstYearToShow}</th>
              <th>Policy {secondYearToShow}</th>
              <th>State</th>
              {isAsesor && (
                <th className='text-end' style={{width: '220px'}}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.allContactsPreview.edges.map(({node}) => {
              const calculateAge = (dateBirth?: string | null): number | string => {
                if (!dateBirth) return '-'
                const birth = new Date(dateBirth)
                const diffMs = Date.now() - birth.getTime()
                const ageDt = new Date(diffMs)
                return Math.abs(ageDt.getUTCFullYear() - 1970)
              }

              const latestPolicy = getLatestPolicy(node)
              const agentName = latestPolicy?.user ? formatUserName(latestPolicy.user) : '-'
              const assignedName = latestPolicy?.assignedTo
                ? formatUserName(latestPolicy.assignedTo)
                : '-'

              const displayName =
                node.fullName ||
                `${node.firstName || ''} ${node.lastName || ''}`.replace(/\s+/g, ' ').trim() ||
                'Unnamed contact'
              const highlightedName = highlightKeyword(displayName)

              const extraYearPolicy = showExtraYear ? getPolicyForYear(node, extraYearToShow) : null
              const firstYearPolicy = getPolicyForYear(node, firstYearToShow)
              const secondYearPolicy = getPolicyForYear(node, secondYearToShow)
              const activePolicy = getLatestActivePolicy(node, now)
              const hasActivePolicy = !!activePolicy

              return (
                <tr key={node.id}>
                  <td dangerouslySetInnerHTML={{__html: `${highlightedName}`}} />
                  <td>{calculateAge(node.dateBirth)}</td>
                  <td>{agentName || '-'}</td>
                  {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT_PREVIEW_ASSIGNED) && (
                    <td>{assignedName || '-'}</td>
                  )}
                  <td>
                    {hasActivePolicy ? (
                      <span className='badge badge-success'>Yes</span>
                    ) : (
                      <span className='badge badge-secondary'>No</span>
                    )}
                  </td>
                  {showExtraYear && <td>{renderPolicyCell(extraYearPolicy, 'No')}</td>}
                  <td>{renderPolicyCell(firstYearPolicy, 'No')}</td>
                  <td>{renderPolicyCell(secondYearPolicy, 'No')}</td>
                  <td>{node.stateEU?.name}</td>
                  {isAsesor && (
                    <td className='text-end'>
                      {(() => {
                        const assignedId = latestPolicy?.assignedTo?.id
                        const assignedNumericId = assignedId
                          ? parseInt(`${parseId(assignedId)}`)
                          : null
                        const canRequestAssignment =
                          isAsesor &&
                          !!latestPolicy &&
                          !!latestPolicy.effectiveDate &&
                          !!assignedNumericId &&
                          !!currentUserId &&
                          assignedNumericId !== currentUserId

                        if (!canRequestAssignment) return null

                        return (
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={() =>
                              handleOpenRequestModal(
                                latestPolicy,
                                assignedName && assignedName !== '-' ? assignedName : null,
                                displayName
                              )
                            }
                          >
                            Request assign to me
                          </Button>
                        )
                      })()}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Modal show={!!requestContext} onHide={() => setRequestContext(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Request assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestContext && (
            <>
              <p className='mb-3'>
                Do you want to request that <strong>{requestContext.policyName}</strong> be assigned
                to you? Current assigned: {requestContext.assignedName || 'Unknown'}.
              </p>
              <Form.Group className='mb-3'>
                <Form.Label>Reason (optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder='Add a note for your supervisor'
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setRequestContext(null)}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSubmitRequest} disabled={requestingAssignment}>
            {requestingAssignment ? 'Sending...' : 'Send request'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal.Body>
  )
}
